/**
 * Clients data hooks.
 *
 * Single source of truth: authenticated sessions only ever see API data —
 * loading yields `isLoading`, failures yield `isError` with empty data (the
 * page shows a banner). Writes never silently fall back — a failed mutation
 * throws so the caller can surface the error and keep the form open. The UI
 * loads one page (limit 100, ample for current data) and filters client-side;
 * the API also supports server-side search/pagination.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { queryKeys, REPORTS_QUERY_PREFIX } from "@/lib/query-client";
import type { Client } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import {
  findInLists,
  patchInList,
  prependToList,
  removeFromList,
  restoreSnapshot,
  snapshotList,
} from "@/lib/optimistic-cache";
import { clientsApi } from "./clients.api";
import type { ApiClient } from "./clients.adapter";
import { toClient } from "./clients.adapter";
import type { ClientFormValues } from "./components/ClientFormModal";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

/** Prefix matching every clients list query (active + archived) — narrower than the whole ["clients"] tree. */
const CLIENTS_LIST_KEY = ["clients", "list"] as const;

export type DataSource = "api" | "mock";

export interface UseClientsResult {
  clients: Client[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useClients(): UseClientsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");

  const query = useQuery({
    queryKey: queryKeys.clientsList(LIST_PARAMS),
    queryFn: () => clientsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data) {
    return {
      clients: query.data.data.map(toClient),
      isLoading: false,
      isError: false,
      isFetching: query.isFetching,
      source: "api",
      error: null,
      refetch: query.refetch,
    };
  }
  if (query.isError) {
    // API failed → surface the error (banner); never swap in fallback data.
    return {
      clients: [],
      isLoading: false,
      isError: true,
      isFetching: false,
      source: "api",
      error: query.error,
      refetch: query.refetch,
    };
  }
  return {
    clients: [],
    isLoading: query.isLoading,
    isError: false,
    isFetching: query.isFetching,
    source: "api",
    error: null,
    refetch: query.refetch,
  };
}

/** Warm the clients list cache (startup warmup — see layout/useWarmupCoreData). */
export function prefetchClients(qc: QueryClient): Promise<void> {
  return qc.prefetchQuery({
    queryKey: queryKeys.clientsList(LIST_PARAMS),
    queryFn: () => clientsApi.list(LIST_PARAMS),
  });
}

export interface UseClientResult {
  client: Client | undefined;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  error: unknown;
}

export function useClient(id: string | undefined): UseClientResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.client(id ?? ""),
    queryFn: () => clientsApi.get(id as string),
    enabled: authed && !!id,
    // Instant paint: seed from the list row the user just clicked (scalars
    // only). `initialDataUpdatedAt: 0` marks the seed as ancient so the real
    // fetch ALWAYS follows to load the relations the list row lacks.
    initialData: () =>
      findInLists<ApiClient>(
        qc,
        [queryKeys.clientsList(LIST_PARAMS), queryKeys.clientsList(ARCHIVED_CLIENTS_PARAMS)],
        id,
      ),
    initialDataUpdatedAt: 0,
  });

  if (query.data)
    return {
      client: toClient(query.data),
      isLoading: false,
      isError: false,
      source: "api",
      error: null,
    };
  if (query.isError)
    return {
      client: undefined,
      isLoading: false,
      isError: true,
      source: "api",
      error: query.error,
    };
  return {
    client: undefined,
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    error: null,
  };
}

const ARCHIVED_CLIENTS_PARAMS = {
  archived: true,
  limit: 100,
  sortBy: "createdAt",
  sortOrder: "desc" as const,
};

export function useArchivedClients() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const query = useQuery({
    queryKey: queryKeys.clientsList(ARCHIVED_CLIENTS_PARAMS),
    queryFn: () => clientsApi.list(ARCHIVED_CLIENTS_PARAMS),
    enabled: authed,
  });
  if (query.data)
    return {
      clients: query.data.data.map(toClient),
      isLoading: false,
      isError: false,
      refetch: query.refetch,
    };
  if (query.isError)
    return { clients: [] as Client[], isLoading: false, isError: true, refetch: query.refetch };
  return {
    clients: [] as Client[],
    isLoading: query.isLoading,
    isError: false,
    refetch: query.refetch,
  };
}

export function useRestoreClient() {
  const qc = useQueryClient();
  const archivedKey = queryKeys.clientsList(ARCHIVED_CLIENTS_PARAMS);
  return useMutation({
    mutationFn: (id: string) => clientsApi.restore(id),
    // Optimistic: the row leaves the archived list immediately; rolled back on error.
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: archivedKey });
      const snapshot = snapshotList(qc, [archivedKey]);
      removeFromList<ApiClient>(qc, archivedKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: (_data, _err, id) => {
      // Reappears in the active list, leaves archived, and re-counts on the dashboard.
      void qc.invalidateQueries({ queryKey: CLIENTS_LIST_KEY });
      void qc.invalidateQueries({ queryKey: queryKeys.client(id) });
      void qc.invalidateQueries({ queryKey: REPORTS_QUERY_PREFIX });
    },
  });
}

/**
 * Create/update/archive/restore. Each call hits the server and invalidates
 * the clients cache; on failure it throws (no silent fallback write).
 */
export function useClientMutations() {
  const qc = useQueryClient();

  const activeKey = queryKeys.clientsList(LIST_PARAMS);
  const archivedKey = queryKeys.clientsList(ARCHIVED_CLIENTS_PARAMS);

  // Background resync (non-blocking): the list is already updated optimistically
  // via setQueryData, so the refetch only reconciles server-computed fields
  // (summary rollups, ordering). Clients feed the dashboard KPIs and recent
  // activity, so every write also refreshes the reports cache — previously
  // missing, which left the dashboard stale for up to 30s after a change.
  const invalidateList = () => void qc.invalidateQueries({ queryKey: CLIENTS_LIST_KEY });
  const invalidateReports = () => void qc.invalidateQueries({ queryKey: REPORTS_QUERY_PREFIX });
  const invalidateOne = (id: string) => {
    void qc.invalidateQueries({ queryKey: CLIENTS_LIST_KEY });
    void qc.invalidateQueries({ queryKey: queryKeys.client(id) });
  };

  return {
    mode: "api" as DataSource,
    create: async (values: ClientFormValues) => {
      // The created row appears at the top of the list the instant the POST
      // resolves — no waiting on the follow-up list refetch.
      const created = await clientsApi.create(values);
      prependToList<ApiClient>(qc, activeKey, created);
      invalidateList();
      invalidateReports();
    },
    update: async (id: string, values: ClientFormValues, opts: { servicesChanged?: boolean } = {}) => {
      // Merge the response into the existing row (and open detail view),
      // preserving the summary/relations the light update response omits.
      const updated = await clientsApi.update(id, values);
      // spec consolidée §1 — les services passent par la route dédiée
      // (permission clients.services-manage), seulement s'ils ont changé.
      if (opts.servicesChanged) await clientsApi.setServices(id, values.serviceIds);
      patchInList<ApiClient>(qc, activeKey, id, updated);
      qc.setQueryData<ApiClient>(queryKeys.client(id), (old) =>
        old ? { ...old, ...updated } : old,
      );
      invalidateOne(id);
      invalidateReports();
    },
    archive: async (id: string) => {
      // Optimistic: the row disappears from the active list before the request
      // resolves; restore the snapshot if the server rejects it.
      const snapshot = snapshotList(qc, [activeKey]);
      removeFromList<ApiClient>(qc, activeKey, id);
      try {
        await clientsApi.archive(id);
      } catch (error) {
        restoreSnapshot(qc, snapshot);
        throw error;
      }
      void qc.invalidateQueries({ queryKey: archivedKey });
      invalidateOne(id);
      invalidateReports();
    },
    restore: async (id: string) => {
      const snapshot = snapshotList(qc, [archivedKey]);
      removeFromList<ApiClient>(qc, archivedKey, id);
      try {
        await clientsApi.restore(id);
      } catch (error) {
        restoreSnapshot(qc, snapshot);
        throw error;
      }
      invalidateOne(id);
      invalidateReports();
    },
  };
}
